import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Link, useParams} from "react-router-dom";
import {Box, Breadcrumbs, Button, CircularProgress, Container, Typography} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import BreadcrumbLink from "../components/BreadcrumbLink";
import {useAlert} from "../context/AlertContext";
import {getPracticeTaskDetails} from "../api/api";
import {PracticeTaskDetailsResponse} from "../types";

const PracticeTaskDetailsPage = () => {
    const {t} = useTranslation();
    const {taskId} = useParams<{ taskId?: string }>();
    const safeTaskId = taskId ?? "";

    const {showAlert} = useAlert();

    const [data, setData] = useState<PracticeTaskDetailsResponse | null>(null);
    const [pageLoading, setPageLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (!safeTaskId) return;

        let cancelled = false;

        const load = async () => {
            setPageLoading(true);
            setHasError(false);

            try {
                const id = Number(safeTaskId);
                const res = await getPracticeTaskDetails(id);

                if (cancelled) return;

                setData(res);
            } catch (e: any) {
                if (cancelled) return;

                setHasError(true);
                setData(null);
                showAlert(e?.message ?? t("practicetaskdetails.errors.loadFailed"), "error");
            } finally {
                if (!cancelled) setPageLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safeTaskId]);

    if (pageLoading && !data) {
        return (
            <Container sx={{mt: 6, display: "flex", justifyContent: "center"}}>
                <CircularProgress/>
            </Container>
        );
    }

    if (hasError) {
        return (
            <Container sx={{mt: 4}}>
                <Button component={Link} to="/" variant="outlined">
                    {t("common.toMainPage")}
                </Button>
            </Container>
        );
    }

    if (!data) return null;

    return (
        <Box sx={{px: {xs: 3, md: 3}}}>
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small"/>}
                maxItems={3}
                itemsBeforeCollapse={1}
                itemsAfterCollapse={2}
                sx={{
                    mb: 2,
                    "& .MuiBreadcrumbs-ol": {
                        overflow: "hidden",
                    },
                    "& .MuiBreadcrumbs-li": {
                        minWidth: 0,
                    },
                }}
            >
                <BreadcrumbLink to="/courses">
                    {t("nav.courses")}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/course/${data.courseId}`}>
                    {data.courseTitle}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/chapter/${data.chapterId}`}>
                    {data.chapterTitle}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/lesson/${data.lessonId}`}>
                    {data.lessonTitle}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/lesson/${data.lessonId}/practices`}>
                    {t("practicetaskdetails.practiceTasks")}
                </BreadcrumbLink>

                <Typography color="text.primary" variant="body2">
                    <strong>{t("practicetaskdetails.details")}</strong>
                </Typography>
            </Breadcrumbs>

            <Box
                sx={(theme) => ({
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                    pb: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    gap: 2,
                })}
            >
                <Typography sx={{fontSize: 28, fontWeight: 800}} noWrap>
                    {data.task.orderNum}. {data.task.title}
                </Typography>

                <Button component={Link} to={-1 as any} variant="outlined" size="small">
                    {t("common.back")}
                </Button>
            </Box>

            <Box
                sx={(theme) => ({
                    mb: 3,
                    pb: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                })}
            >
                <Typography
                    variant="body1"
                    dangerouslySetInnerHTML={{__html: data.task.content || ""}}
                />
            </Box>
        </Box>
    );
};

export default PracticeTaskDetailsPage;